import { useState } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import CommonModal from '../../common/Modal';
import PortfolioChooserOption from '../subscribe/PortfolioChooserOption.jsx';
import SubscribePortfolioActivitiesModal from '../subscribe/SubscribePortfolioActivitiesModal.jsx';
import useParentPortfolios from '../../../hooks/useParentPortfolios';
import clickableRowProps from '../../../helpers/a11y/clickableRowProps';

/**
 * Add-chooser for Activiteiten: pick a source (portfolio subscribe per parent,
 * or "eigen") in an options modal, then run that source's flow. Used by the
 * standalone Activiteiten page (ActivityManager) and the TOC navigator.
 * AI-CONTEXT: See docs/features/toc-navigator-add-chooser.md
 * AI-CONTEXT: See docs/patterns.md#ui-add-chooser-flow
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the chooser modal is open
 * @param {(open: boolean) => void} props.setOpen - Setter to close the chooser modal
 * @param {() => void} props.onOwnAdd - Called when the user picks "Eigen activiteit toevoegen"
 * @param {(added: Array<{id: string, docid: string}>) => void} [props.onAdded] - Called with the locally created activity after a whole-activity portfolio subscribe
 */
const AddActivityFlow = ({ open, setOpen, onOwnAdd, onAdded }) => {
    const [subscribeModalOpen, setSubscribeModalOpen] = useState(false);
    const [subscribePortfolioId, setSubscribePortfolioId] = useState('');
    const { parentPortfolioIds, portfolioNameById } = useParentPortfolios();

    return (
        <>
            <CommonModal
                modalOpen={open}
                setModalOpen={setOpen}
                pageHeader={'Activiteit toevoegen'}
                hideConfirm={true}
                content={
                    <div className="options-modal-list">
                        {parentPortfolioIds.map((portfolioId) => (
                            <PortfolioChooserOption
                                key={portfolioId}
                                portfolioId={portfolioId}
                                portfolioName={portfolioNameById[portfolioId]}
                                itemNoun="Activiteiten"
                                itemNounLower="activiteiten"
                                onClick={() => {
                                    setOpen(false);
                                    setSubscribePortfolioId(portfolioId);
                                    setSubscribeModalOpen(true);
                                }}
                            />
                        ))}
                        <div
                            className="options-modal-item" {...clickableRowProps(() => { setOpen(false); onOwnAdd(); })}
                        >
                            <div className="options-modal-item__icon options-modal-item__icon--add">
                                <AddIcon />
                            </div>
                            <div className="options-modal-item__body">
                                <p className="options-modal-item__title">Eigen activiteit toevoegen</p>
                                <p className="options-modal-item__description">
                                    Beschrijf zelf een activiteit — vrij in te vullen, geen sjabloon.
                                </p>
                            </div>
                            <ChevronRightIcon className="options-modal-item__chevron" />
                        </div>
                    </div>
                }
            />

            {subscribeModalOpen && (
                <SubscribePortfolioActivitiesModal
                    modalOpen={subscribeModalOpen}
                    setModalOpen={setSubscribeModalOpen}
                    preselectedPortfolioId={subscribePortfolioId}
                    onSubscribed={onAdded}
                />
            )}
        </>
    );
};

export default AddActivityFlow;
